import { createContext, useContext, useRef, useEffect, useCallback, useMemo } from 'react';

/* ── Unsaved-changes guard ───────────────────────────────────────────────
   A tiny app-wide flag that a form sets while it has unsaved edits, so we can
   warn before the edits are silently lost. Used by the doctor Consultation
   form (each section reports its `dirty` via ConsultSectionHeader); the section
   nav, the shell nav links and the browser's own tab-close all check it.

   The flag lives in a ref (not state) so toggling it never re-renders the app.
   `beforeunload` covers tab close / refresh / external navigation; in-app
   React-Router navigation is guarded at the click sites (they call isDirty()). */

const UnsavedGuardContext = createContext(null);

export function UnsavedGuardProvider({ children }) {
  const dirtyRef = useRef(false);

  const setDirty = useCallback((v) => { dirtyRef.current = !!v; }, []);
  const isDirty = useCallback(() => dirtyRef.current, []);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const value = useMemo(() => ({ setDirty, isDirty }), [setDirty, isDirty]);
  return <UnsavedGuardContext.Provider value={value}>{children}</UnsavedGuardContext.Provider>;
}

/* Safe no-op when no provider is mounted, so consumers never crash. */
const NOOP = { setDirty: () => {}, isDirty: () => false };
export function useUnsavedGuard() {
  return useContext(UnsavedGuardContext) || NOOP;
}
